import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    ITipperDetail,
    TipperCreatePayload,
    TipperRestoreDeletedPayload,
    TipperUpdatePayload,
} from "@/customTypings/tipper/Tipper";
import { authHeader, handleResponse } from "../../utils";
import { postTipperCreate } from "@/utils/tipper/tipperCreateConflict";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";

function siteTippersBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/tippers`;
}

export const tippersService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(siteId: string, id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`${siteTippersBase(siteId)}/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<ITipperDetail>;
}

function create(siteId: string, payload: TipperCreatePayload) {
    return postTipperCreate<ITipperDetail>(siteId, payload);
}

function restoreDeletedWithCreateValues(siteId: string, payload: TipperRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteTippersBase(siteId)}/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<ITipperDetail>;
}

function update(siteId: string, payload: TipperUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteTippersBase(siteId)}/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<ITipperDetail>>;
}

function disable(siteId: string, id: string) {
    return postQuery(siteId, "disable", id);
}

function enable(siteId: string, id: string) {
    return postQuery(siteId, "enable", id);
}

function softDelete(siteId: string, id: string) {
    return postQuery(siteId, "softdelete", id);
}

function restore(siteId: string, id: string) {
    return postQuery(siteId, "restore", id);
}

function postQuery(siteId: string, action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteTippersBase(siteId)}/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(
        handleResponse
    );
}
